var debounce = debounce || function(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this._connected = false;
      this._abortController = null;
      this._mobileNavTimer = null;
      this._scrollRafId = null;
    }
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abortController = new AbortController();
      const { signal } = this._abortController;
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE' && this.toggle.hasAttribute('open')) {
            document.body.classList.remove('overflow-hidden');
            this.menu.querySelectorAll('details[open]').forEach((details) => {
              details.classList.remove('menu-opening', 'submenu-open');
              details.removeAttribute('open');
            });
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
            this.toggle.querySelector('.mobile-toggle').focus();
          }
        }
      }, { signal });
      this._stickyOffset = 0;
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
        this._stickyOffset = this.offsetTop;
        window.addEventListener('resize', debounce(() => {
          this._stickyOffset = this.offsetTop;
        }, 100), { passive: true, signal });
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
      }, { signal });

      // Consolidated scroll listener with rAF coalescing
      const onScroll = () => {
        if (this._scrollRafId) return;
        this._scrollRafId = requestAnimationFrame(() => {
          this._scrollRafId = null;
          this.setStickyClass();
          this.setHeaderOffset();
          this.setHeaderHeight();
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true, signal });

      onScroll();

      if (document.querySelector('.announcement-bar-section')) {
        this.setAnnouncementHeight();
        window.addEventListener('resize', debounce(() => {
          this.setAnnouncementHeight();
        }, 100), { passive: true, signal });
      }

      // Mobile Navigation transparent header support.
      this._mobileNavTimer = setTimeout(() => {

        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
      this.menu.addEventListener('click', (event) => this.onMenuClick(event), { signal });
    }
    disconnectedCallback() {
      this._connected = false;
      this._abortController?.abort();
      this._abortController = null;
      clearTimeout(this._mobileNavTimer);
      this._mobileNavTimer = null;
      if (this._scrollRafId) cancelAnimationFrame(this._scrollRafId);
      this._scrollRafId = null;
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', window.scrollY >= this._stickyOffset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      let h = a_bar.clientHeight;
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    onMenuClick(event) {
      const closeButton = event.target.closest('.parent-link-back--button');
      if (closeButton && this.menu.contains(closeButton)) {
        this.onCloseButtonClick(event, closeButton);
        return;
      }
      const summaryElement = event.target.closest('summary');
      if (summaryElement && this.menu.contains(summaryElement)) this.onSummaryClick(summaryElement);
    }
    onSummaryClick(summaryElement) {
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');

      if (isOpen) {
        detailsElement.classList.remove('menu-opening', 'submenu-open');
        return;
      }

      // Hydrate deferred sub-menu on first open
      const tpl = detailsElement.querySelector(':scope > .mobile-menu-deferred');
      if (tpl) {
        detailsElement.appendChild(tpl.content);
        tpl.remove();
      }

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event, closeButton) {
      event.preventDefault();
      const detailsElement = closeButton.closest('details');
      const summaryElement = detailsElement.querySelector(':scope > summary');
      this.closeSubmenu(detailsElement);
      setTimeout(() => summaryElement && summaryElement.focus(), 400);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening', 'submenu-open');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
      this.desktopMedia = window.matchMedia('(min-width: 1068px)');
      this._mediaController = null;
      this._resizeController = null;
      this.onMediaChange = this.onMediaChange.bind(this);
    }
    connectedCallback() {
      if (!this.submenus.length || this._mediaController) return;
      this._mediaController = new AbortController();
      this.desktopMedia.addEventListener('change', this.onMediaChange, { signal: this._mediaController.signal });
      this.onMediaChange();
    }
    disconnectedCallback() {
      this._mediaController?.abort();
      this._mediaController = null;
      this.deactivate();
    }
    onMediaChange() {
      if (this.desktopMedia.matches) {
        this.activate();
      } else {
        this.deactivate();
      }
    }
    activate() {
      if (this._resizeController) return;
      this._resizeController = new AbortController();
      const { signal } = this._resizeController;
      const _this = this;
      document.fonts.ready.then(function() {
        if (signal.aborted || !_this.isConnected || !_this.desktopMedia.matches) return;
        _this.resizeSubMenus();
        window.addEventListener('resize', debounce(function() {
          _this.resizeSubMenus();
        }, 100), { passive: true, signal });
      });
    }
    deactivate() {
      this._resizeController?.abort();
      this._resizeController = null;
    }
    resizeSubMenus() {
      let updates = [];
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;
          updates.push({ el: sub_submenu.parentElement, needsLeft: total > window.innerWidth });
        });
      });
      updates.forEach(({ el, needsLeft }) => {
        el.classList.toggle('left-submenu', needsLeft);
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
